import { useToast } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { useState } from "react";
import { useIntl } from "react-intl";

import { TenantRole, UserRead } from "@nens/rana/api-v1";
import { useTenantListQuery } from "@nens/ranalib";
import { SchemaForFormData, UiSchemaForFormData } from "@nens/shared-utils";

import { useTenantAllQuery } from "@/queries/query-tenant";
import { useUserQueriesWithTenantId } from "@/queries/query-user";
import { userRoleString } from "@/translations";

import {
  UpdateUsersAcrossTenant,
  useUpdateUsersAcrossTenants,
} from "../api/update-user-role-across-tenants";

export type AdminTenantUserEdit = {
  id: string;
  identityProviderId: string;
  givenName: string;
  familyName: string;
  email: string;
  organisationInfos: { organisationId: string; role: TenantRole; active: boolean }[];
};

export const useTenantUserFormSchema = () => {
  const intl = useIntl();
  const { data: allTenants, isLoading } = useTenantAllQuery();

  const schema: SchemaForFormData<AdminTenantUserEdit> = {
    type: "object",
    required: ["id", "identityProviderId", "organisationInfos", "familyName", "givenName", "email"],
    properties: {
      id: {
        type: "string",
        title: intl.formatMessage({ id: "tenant_user.id", defaultMessage: "User ID" }),
        readOnly: true,
      },
      identityProviderId: {
        type: "string",
        title: intl.formatMessage({
          id: "tenant_user.identity_provider_id",
          defaultMessage: "Identity Provider ID",
        }),
        readOnly: true,
      },
      familyName: {
        type: "string",
        title: intl.formatMessage({ id: "tenant_user.family_name", defaultMessage: "Family Name" }),
        readOnly: true,
      },
      givenName: {
        type: "string",
        title: intl.formatMessage({ id: "tenant_user.given_name", defaultMessage: "Given Name" }),
        readOnly: true,
      },
      email: {
        type: "string",
        format: "email",
        title: intl.formatMessage({ id: "tenant_user.email", defaultMessage: "Email" }),
        readOnly: true,
      },
      organisationInfos: {
        type: "array",
        title: intl.formatMessage({
          id: "tenant_user.roles_and_organisations",
          defaultMessage: "Roles and Organisations",
        }),
        description: intl.formatMessage({
          id: "tenant_user.roles_and_organisations.description",
          defaultMessage:
            "Define the roles of the user in different organisations. To remove the user from an organisation, remove the corresponding entry (they will be added back when the logging in If they have an identity provider like for example NelenSchuurmans, otherwise their access will be revoked). To disable a user from logging into a certain tenant decheck the active checkbox.",
        }),
        items: {
          type: "object",
          required: ["role", "organisationId", "active"],
          properties: {
            role: {
              type: "string",
              title: intl.formatMessage({ id: "tenant_user.role", defaultMessage: "Role" }),
              enum: Object.values(TenantRole),
              default: TenantRole.Member,
            },
            organisationId: {
              type: "string",
              title: intl.formatMessage({
                id: "tenant_user.organisation_id",
                defaultMessage: "Organisation",
              }),
              enum: allTenants?.items.map((tenant) => tenant.id) || [],
            },
            active: {
              type: "boolean",
              title: intl.formatMessage({
                id: "tenant_user.active",
                defaultMessage: "Active",
              }),
              default: true,
            },
          },
        },
      },
    },
  };

  const uiSchema: UiSchemaForFormData<AdminTenantUserEdit> = {
    id: {
      "ui:placeholder": intl.formatMessage({
        id: "tenant_user.id.placeholder",
        defaultMessage: "User ID",
      }),
    },
    identityProviderId: {
      "ui:placeholder": intl.formatMessage({
        id: "tenant_user.identity_provider_id.placeholder",
        defaultMessage: "Identity Provider ID",
      }),
    },
    familyName: {
      "ui:placeholder": intl.formatMessage({
        id: "tenant_user.family_name.placeholder",
        defaultMessage: "Enter family name",
      }),
    },
    givenName: {
      "ui:placeholder": intl.formatMessage({
        id: "tenant_user.given_name.placeholder",
        defaultMessage: "Enter given name",
      }),
    },
    email: {
      "ui:placeholder": intl.formatMessage({
        id: "tenant_user.email.placeholder",
        defaultMessage: "Enter email",
      }),
    },
    organisationInfos: {
      "ui:options": { label: true },
      items: {
        "ui:order": ["organisationId", "role", "active"],
        role: {
          "ui:placeholder": intl.formatMessage({
            id: "tenant_user.role.placeholder",
            defaultMessage: "Select role",
          }),
          "ui:enumNames": Object.values(TenantRole).map((role) => userRoleString(intl, role)),
        },
        organisationId: {
          "ui:placeholder": intl.formatMessage({
            id: "tenant_user.organisation_id.placeholder",
            defaultMessage: "Select organisation",
          }),
        },
        active: {},
      },
    },
  };

  return { schema, uiSchema, isLoading };
};

export const useAdminTenantUserDetail = (user: UserRead) => {
  const toast = useToast();
  const intl = useIntl();
  const { mutateAsync: updateUsersAcrossTenants, isMutating } = useUpdateUsersAcrossTenants(
    user.id
  );
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const tenantListQuery = useTenantListQuery(user.id);

  const tenantList = tenantListQuery.data?.items ?? [];

  const rolePerTenantQueries = useUserQueriesWithTenantId(
    user.id,
    tenantList.map((tenant) => tenant.id)
  );

  const isLoading =
    tenantListQuery.isLoading ||
    rolePerTenantQueries.some((query) => query.isLoading) ||
    isMutating;

  if (isLoading) return { isLoading: true } as { isLoading: true };

  const formData: AdminTenantUserEdit = {
    id: user.id,
    identityProviderId: user.identityProviderId,
    givenName: user.givenName,
    familyName: user.familyName,
    email: user.email,
    organisationInfos: tenantList.map((tenant, idx) => {
      const role = rolePerTenantQueries[idx].data?.role ?? undefined;
      const active = rolePerTenantQueries[idx].data?.active ?? undefined;
      if (!role) throw Error("role is undefined");
      if (active === undefined) throw Error("active is undefined");
      return {
        organisationId: tenant.id,
        role,
        active,
      };
    }),
  };

  const onSubmit = async (newUserInfo: AdminTenantUserEdit) => {
    const updates: UpdateUsersAcrossTenant[] = [];

    if (!isEqual(formData.organisationInfos, newUserInfo.organisationInfos)) {
      if (!newUserInfo.organisationInfos.length) {
        // All organisations removed, open delete modal
        return setDeleteModalOpen(true);
      }

      newUserInfo.organisationInfos.forEach((orgInfo) => {
        const existingOrgInfo = formData.organisationInfos.find(
          (originalorgInfo) => originalorgInfo.organisationId === orgInfo.organisationId
        );

        if (!existingOrgInfo) {
          return updates.push({
            type: "add-to-org",
            ...orgInfo,
            ...newUserInfo,
          });
        }

        if (isEqual(existingOrgInfo, orgInfo)) {
          return null;
        }

        return updates.push({ type: "update-org-info", ...orgInfo });
      });

      formData.organisationInfos.forEach((originalOrgInfo) => {
        const existsInNew = newUserInfo.organisationInfos.find(
          (orgInfo) => orgInfo.organisationId === originalOrgInfo.organisationId
        );

        if (!existsInNew) {
          updates.push({
            type: "delete-from-org",
            organisationId: originalOrgInfo.organisationId,
          });
        }
      });
    }

    if (updates.length) {
      await updateUsersAcrossTenants(updates);
      toast({
        title: intl.formatMessage({ id: "success", defaultMessage: "Success!" }),
        description: `User updated successfully.`,
        status: "success",
      });
    } else {
      toast({
        title: "No changes!",
        description: `No changes detected.`,
        status: "warning",
      });
    }
  };

  return {
    isLoading: false,
    formData,
    onSubmit,
    isDeleteModalOpen,
    tenantList,
    setDeleteModalOpen,
  } as const;
};
